/* Swym wishlist bridge — the ONE controller for every designed heart.
 *
 * Covers all `.swym-add-to-wishlist[data-product-handle]` buttons theme-wide:
 * product cards (collection grid, PDP recs, cart recs), the PDP buy-box pair
 * (mobile heart square + desktop text link), and the quiz results hearts
 * (client-rendered into the drawer long after page load).
 *
 * Why this exists (2026-07-21): the old bridges (in product-card.js and
 * main-product.js) forwarded clicks to Swym's auto-injected buttons and
 * mirrored their DOM state via MutationObservers. Swym's relay build REPLACES
 * those button nodes on every list change, killing the observers, and the
 * fixed post-click sync timers raced Swym's server roundtrip — hearts drifted
 * from the real list. This bridge talks to Swym's documented v3 API instead
 * and never depends on the injected buttons (which stay hidden via CSS):
 *
 *   - window.SwymCallbacks.push(fn)            → init hook, delivers `swat`
 *   - swat.fetchLists({callbackFn, errorFn})   → ground-truth saved items
 *   - swat.addToWishlistUsingDu({du, epi}, cb, errCb)
 *   - swat.removeFromWishlistUsingDu({du, epi}, cb, errCb)
 *   - swat.evtLayer 'sw:addedtowishlist' / 'sw:removedfromwishlist' events
 *     (fired for OUR calls and for drawer/other-tab changes alike — this is
 *     what keeps hearts truthful after removals in the wishlist drawer)
 *
 * NB Swym's runtime is consent-gated (Shopify customerPrivacy → Pandectes
 * banner): until consent exists SwymCallbacks never fires and hearts stay
 * outline no-ops. That gate is store-level config, not fixable here.
 */
(function () {
  'use strict';

  var HEART = '.swym-add-to-wishlist[data-product-handle]';

  var swat = null;
  var saved = {};   // handle → { epis: [id, …] } while in the wishlist
  var busy = {};    // handle → true while an add/remove roundtrip is in flight
  var wishlistLid = null;   // id of the 'wl' list — see the fetchLists note below

  function handleFromDu(du) {
    var i = (du || '').indexOf('/products/');
    return i === -1 ? '' : du.slice(i + 10).split(/[/?#]/)[0];
  }

  function duFor(handle) {
    return window.location.origin + '/products/' + handle;
  }

  /* Paint every heart for a handle (or all hearts when handle is null). */
  function paint(handle, root) {
    var scope = root || document;
    scope.querySelectorAll(HEART).forEach(function (h) {
      var k = h.dataset.productHandle;
      if (handle && k !== handle) return;
      var inWl = !!saved[k];
      h.classList.toggle('is-in-wishlist', inWl);
      h.setAttribute('aria-pressed', inWl ? 'true' : 'false');
    });
  }

  function rememberItem(item) {
    var k = handleFromDu(item && item.du);
    if (!k) return '';
    var entry = saved[k] || (saved[k] = { epis: [] });
    if (item.epi && entry.epis.indexOf(item.epi) === -1) entry.epis.push(item.epi);
    return k;
  }

  function forgetItem(item) {
    var k = handleFromDu(item && item.du);
    if (!k || !saved[k]) return k;
    if (item.epi) {
      saved[k].epis = saved[k].epis.filter(function (e) { return e !== item.epi; });
      if (saved[k].epis.length === 0) delete saved[k];
    } else {
      delete saved[k];
    }
    return k;
  }

  /* ── Click handling ──
     PDP hearts sit inside the product form — save the SELECTED size (the form's
     variant input). Card/quiz hearts have no size context → Swym's default
     (first variant). Optimistic paint, reverted if the API errors. */
  function onClick(e) {
    var heart = e.target.closest(HEART);
    if (!heart) return;
    e.preventDefault();
    if (!swat) { askConsent(heart); return; }   // Swym not loaded yet (consent gate)
    toggle(heart);
  }

  function toggle(heart) {
    var handle = heart.dataset.productHandle;
    if (!handle || busy[handle]) return;
    busy[handle] = true;

    var wasSaved = !!saved[handle];
    var done = function () { delete busy[handle]; };
    var revert = function () {
      // Repaint from the store (events may have already corrected it).
      paint(handle);
      done();
    };

    if (wasSaved) {
      // Remove every saved variant of this product.
      var epis = saved[handle].epis.slice();
      delete saved[handle];
      paint(handle);   // optimistic — outline now
      var pending = epis.length || 1;
      var next = function () { if (--pending <= 0) done(); };
      if (!epis.length) {
        swat.removeFromWishlistUsingDu({ du: duFor(handle) }, next, revert);
      } else {
        epis.forEach(function (epi) {
          swat.removeFromWishlistUsingDu({ du: duFor(handle), epi: epi }, next, revert);
        });
      }
    } else {
      var payload = { du: duFor(handle) };
      // Inside the PDP buy box? Use the currently-selected variant.
      var form = heart.closest('form');
      var variantInput = form && form.querySelector('[data-variant-id-input]');
      if (variantInput && variantInput.value) payload.epi = parseInt(variantInput.value, 10);
      saved[handle] = { epis: payload.epi ? [payload.epi] : [] };
      paint(handle);   // optimistic — filled now
      swat.addToWishlistUsingDu(payload, done, function () {
        delete saved[handle];
        revert();
      });
    }
  }

  /* ── Consent prompt ──
     Swym's loader (the Wishlist Plus app embed) only starts once Shopify's
     customerPrivacy says preferences processing is allowed, so a shopper who
     hasn't answered the Pandectes banner, or declined it, gets hearts with no
     Swym behind them. The banner can't be reopened from code on this store
     (Pandectes' JS API is plan-gated and its #reopenBanner trigger is off), and
     Swym's own consent popup ships in an embed this install doesn't offer. So a
     heart tapped before Swym is up opens this prompt; "Allow and save" grants
     the preferences category only (setTrackingConsent is a partial update, the
     shopper's other answers stand). Swym's loader listens for
     visitorConsentCollected and loads at once, and the init below then saves
     the tapped product. */
  var pendingHeart = null;
  var pendingTimer = null;
  var prompt = null;

  function privacyApi(cb) {
    var sh = window.Shopify;
    if (sh && sh.customerPrivacy) return cb(sh.customerPrivacy);
    if (!sh || !sh.loadFeatures) return cb(null);
    sh.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      cb(err ? null : window.Shopify.customerPrivacy || null);
    });
  }

  function queueSave(heart) {
    pendingHeart = heart;
    clearTimeout(pendingTimer);
    // Give up quietly if Swym never arrives (blocked script, network).
    pendingTimer = setTimeout(function () { pendingHeart = null; }, 15000);
  }

  function askConsent(heart) {
    privacyApi(function (privacy) {
      if (!privacy || !privacy.setTrackingConsent) return;
      // Consent already given, Swym still loading: save once it's up.
      if (privacy.preferencesProcessingAllowed && privacy.preferencesProcessingAllowed()) {
        queueSave(heart);
        return;
      }
      showPrompt(heart, privacy);
    });
  }

  function buildPrompt() {
    var t = (window.notedStrings && window.notedStrings.wishlist_consent) || {};
    var el = document.createElement('div');
    el.className = 'wishlist-consent';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-labelledby', 'wishlist-consent-message');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<p id="wishlist-consent-message"></p>' +
      '<div class="wishlist-consent__actions">' +
        '<button type="button" class="wishlist-consent__btn wishlist-consent__btn--dismiss" data-consent-dismiss></button>' +
        '<button type="button" class="wishlist-consent__btn wishlist-consent__btn--allow" data-consent-allow></button>' +
      '</div>';
    el.querySelector('p').textContent = t.message || '';
    el.querySelector('[data-consent-dismiss]').textContent = t.dismiss || '';
    el.querySelector('[data-consent-allow]').textContent = t.allow || '';
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hidePrompt();
    });
    document.body.appendChild(el);
    return el;
  }

  function showPrompt(heart, privacy) {
    if (!prompt) prompt = buildPrompt();
    prompt._heart = heart;
    prompt.querySelector('[data-consent-allow]').onclick = function () {
      queueSave(heart);
      hidePrompt();
      privacy.setTrackingConsent({ preferences: true }, function () {});
    };
    prompt.querySelector('[data-consent-dismiss]').onclick = hidePrompt;
    prompt.setAttribute('aria-hidden', 'false');
    prompt.classList.add('is-visible');
    prompt.querySelector('[data-consent-allow]').focus();
  }

  function hidePrompt() {
    if (!prompt || !prompt.classList.contains('is-visible')) return;
    prompt.classList.remove('is-visible');
    prompt.setAttribute('aria-hidden', 'true');
    if (prompt._heart && document.contains(prompt._heart)) prompt._heart.focus();
    prompt._heart = null;
  }

  // Save the heart tapped before Swym was up, unless it's already in the list.
  function runPending() {
    var heart = pendingHeart;
    pendingHeart = null;
    clearTimeout(pendingTimer);
    if (!heart || !document.contains(heart)) return;
    if (saved[heart.dataset.productHandle]) return;
    toggle(heart);
  }

  /* ── Swym init ── */
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (s) {
    swat = s;

    // Ground truth: the WISHLIST's contents → saved store → paint everything.
    //
    // fetchLists returns EVERY list Swym holds for the visitor, not just the
    // wishlist — Save For Later is enabled on this store (it has its own list,
    // fed when a shopper removes a cart line) and Swym also holds browse-time
    // saves. Merging all of them painted filled hearts on products the shopper
    // never favourited, while the drawer — which only ever renders "My Wishlist"
    // — showed nothing: "the love heart is coloured in, suggesting it's been
    // added to favourites. It hasn't." So keep `lty === 'wl'` and drop the rest.
    swat.fetchLists({
      callbackFn: function (lists) {
        saved = {};
        (lists || []).forEach(function (list) {
          if (list.lty !== 'wl') return;
          wishlistLid = list.lid || wishlistLid;
          (list.listcontents || []).forEach(rememberItem);
        });
        paint(null);
        runPending();
      },
      errorFn: function () { runPending(); /* leave hearts as-is; events still correct them */ }
    });

    // Live sync — fires for our own calls AND external changes (wishlist
    // drawer removals, other tabs). Event names come from swat.JSEvents so we
    // never hardcode a guess. Payload: e.detail.d = the list item.
    var evts = swat.JSEvents || {};
    var on = function (name, fn) {
      if (name && swat.evtLayer) swat.evtLayer.addEventListener(name, fn);
    };
    // Same guard on the live events: only act on items that belong to the
    // wishlist. When either id is unknown (brand-new visitor with no list yet,
    // or a payload without one) fall through and treat it as the wishlist,
    // which is the pre-existing behaviour.
    var fromAnotherList = function (item) {
      return !!(item && item.lid && wishlistLid && item.lid !== wishlistLid);
    };

    on(evts.addedToWishlist, function (e) {
      var item = e.detail && (e.detail.d || e.detail);
      if (fromAnotherList(item)) return;
      paint(rememberItem(item));
    });
    on(evts.removedFromWishlist, function (e) {
      var item = e.detail && (e.detail.d || e.detail);
      if (fromAnotherList(item)) return;
      paint(forgetItem(item));
    });
  });

  /* ── Late-rendered hearts (quiz results drawer, cart-drawer recs) ──
     Paint any heart that enters the DOM after the initial fetch. */
  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches(HEART)) paint(n.dataset.productHandle);
        else if (n.querySelector && n.querySelector(HEART)) paint(null, n);
      }
    }
  });

  function init() {
    document.addEventListener('click', onClick);
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
